<template>
    <b-modal
        :id="modalId"
        title="Contact Patient - MedSync"
        size="lg"
        @show="onShow"
        @hidden="onHidden"
    >
        <template #modal-footer="{ cancel }">
            <b-button variant="secondary" @click="cancel()">Cancel</b-button>
            <b-button variant="success" @click="saveContactAttempt" :disabled="!isValid || saving">
                <b-spinner small v-if="saving"></b-spinner>
                {{ saving ? 'Saving...' : 'Save Contact' }}
            </b-button>
        </template>

        <b-container v-if="queueItem" fluid>
            <!-- Patient Information -->
            <b-row class="mb-3">
                <b-col>
                    <h5>Patient Information</h5>
                    <b-card>
                        <b-row>
                            <b-col cols="12" md="6">
                                <strong>Patient Name:</strong>
                                <router-link :to="`/processing/patient-dashboard/${queueItem.patientId}`" target="_blank">
                                    {{ queueItem.patientName }}
                                </router-link>
                            </b-col>
                        </b-row>
                        <b-row class="mt-2">
                            <b-col cols="12" md="6">
                                <strong>Anchor Date:</strong> {{ formatDate(queueItem.anchorDate) }}
                            </b-col>
                            <b-col cols="12" md="6">
                                <strong>Days Until Anchor:</strong>
                                <span :class="getDaysUntilClass(queueItem.daysUntilAnchor)">
                                    {{ queueItem.daysUntilAnchor }}
                                </span>
                            </b-col>
                        </b-row>
                        <b-row class="mt-2">
                            <b-col cols="12" md="6">
                                <strong># of Medications:</strong> {{ queueItem.medicationCount }}
                            </b-col>
                            <b-col cols="12" md="6">
                                <strong># Needing New Rx:</strong>
                                <span :class="{ 'text-warning font-weight-bold': queueItem.needsNewRxCount > 0 }">
                                    {{ queueItem.needsNewRxCount }}
                                </span>
                            </b-col>
                        </b-row>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Medications List -->
            <b-row class="mb-3" v-if="medications && medications.length > 0">
                <b-col>
                    <h5>Medications</h5>
                    <b-table
                        :items="medications"
                        :fields="medicationFields"
                        striped
                        small
                    >
                        <template #cell(drugName)="data">
                            {{ data.item.drugName }}
                        </template>
                        <template #cell(refillsRemaining)="data">
                            <span :class="{ 'text-danger font-weight-bold': data.item.refillsRemaining === 0 }">
                                {{ data.item.refillsRemaining ?? 'N/A' }}
                            </span>
                        </template>
                    </b-table>
                </b-col>
            </b-row>

            <!-- Contact Status -->
            <b-row class="mb-3">
                <b-col>
                    <label for="contact-status">Contact Status <span class="text-danger">*</span></label>
                    <b-form-select
                        id="contact-status"
                        v-model="contactStatus"
                        :options="contactStatusOptions"
                        required
                    ></b-form-select>
                </b-col>
            </b-row>

            <!-- Notes -->
            <b-row class="mb-3">
                <b-col>
                    <label for="contact-notes">Notes</label>
                    <AutoCompleteTextarea
                        id="contact-notes"
                        v-model="notes"
                        rows="4"
                        class="textarea-autosize"
                        placeholder="Enter any notes about the contact attempt..."
                    />
                </b-col>
            </b-row>

            <!-- Loading State -->
            <b-row v-if="loadingMedications">
                <b-col class="text-center">
                    <b-spinner small></b-spinner> Loading medications...
                </b-col>
            </b-row>
        </b-container>
    </b-modal>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import AutoCompleteTextarea from '@/components/AutoCompleteTextarea.vue';
import Axios from 'axios';
import moment from 'moment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { MedSyncQueueItem, MedSyncMedication } from "@/models/MedSync";

@Component({
    name: 'ContactPatientModal',
    components: { AutoCompleteTextarea },
})
export default class ContactPatientModal extends Vue {
    @Prop({ default: 'contact-patient-modal' })
    modalId!: string;

    private queueItem: MedSyncQueueItem | null = null;
    private medications: MedSyncMedication[] = [];
    private loadingMedications = false;
    private saving = false;
    private contactStatus = '';
    private notes = '';

    private contactStatusOptions = [
        { value: '', text: 'Please select...', disabled: true },
        { value: 'Contacted', text: 'Contacted' },
        { value: 'Left Message', text: 'Left Message' },
        { value: 'No Answer', text: 'No Answer' },
        { value: 'Confirmed', text: 'Confirmed' },
        { value: 'Declined', text: 'Declined' },
    ];

    private medicationFields = [
        { key: 'drugName', label: 'Drug Name' },
        { key: 'rxID', label: 'Rx ID' },
        { key: 'daySupply', label: 'Day Supply' },
        { key: 'refillsRemaining', label: 'Refills Remaining' },
    ];

    get isValid(): boolean {
        return !!this.contactStatus;
    }

    async show(item: MedSyncQueueItem) {
        this.queueItem = item;
        this.$bvModal.show(this.modalId);
    }

    onShow() {
        if (this.queueItem) {
            this.loadMedications();
        }
    }

    onHidden() {
        // Reset form
        this.queueItem = null;
        this.medications = [];
        this.contactStatus = '';
        this.notes = '';
        this.loadingMedications = false;
        this.saving = false;

        // Notify parent that modal is hidden
        this.$emit('hidden');
    }

    async loadMedications() {
        if (!this.queueItem) return;

        this.loadingMedications = true;
        try {
            const response = await Axios.get(`/api/medsync/enrollment/${this.queueItem.enrollmentId}/medications`);
            this.medications = response.data;
        } catch (error) {
            console.error('Error loading medications:', error);
            this.$notification(NotificationOptions.error('Failed to load medications'));
        } finally {
            this.loadingMedications = false;
        }
    }

    async saveContactAttempt() {
        if (!this.queueItem || !this.isValid) return;

        this.saving = true;
        try {
            await Axios.post(`/api/medsync/enrollment/${this.queueItem.enrollmentId}/contact`, {
                contactStatus: this.contactStatus,
                notes: this.notes
            });

            this.$notification(NotificationOptions.success('Contact attempt logged successfully'));
            this.$emit('contact-saved', this.queueItem.enrollmentId);
            this.$bvModal.hide(this.modalId);
        } catch (error) {
            console.error('Error saving contact attempt:', error);
            this.$notification(NotificationOptions.error('Failed to save contact attempt'));
        } finally {
            this.saving = false;
        }
    }

    getDaysUntilClass(days: number): string {
        if (days <= 3) {
            return 'text-danger font-weight-bold';
        } else if (days <= 7) {
            return 'text-warning font-weight-bold';
        }
        return '';
    }

    formatDate(dateString: string): string {
        return moment.utc(dateString).local().format('MM/DD/YYYY');
    }
}
</script>

<style scoped>
</style>
